import { Decimal } from 'decimal.js';
import { GraphQLError, GraphQLScalarType, Kind } from 'graphql';
import { clamp } from 'lodash';

import { GRAT_ANNUITIES_SUPPORTED_DECIMAL_PLACES } from '@/modules/gratAnnuities/gratAnnuities.constants';

export type Percent = Decimal;
export const GraphQLPercent: GraphQLScalarType =
  /*#__PURE__*/ new GraphQLScalarType({
    name: 'Percent',

    description: 'Our Custom Percent representation',

    serialize(value) {
      if (!Decimal.isDecimal(value)) {
        throw new TypeError(`Percent value is not Decimal: ${value as string}`);
      }

      return value.toString();
    },

    parseValue(value) {
      if (typeof value !== 'string') {
        throw new TypeError(`Percent value is not string: ${value as string}`);
      }

      return new Decimal(value);
    },

    parseLiteral(ast) {
      if (ast.kind !== Kind.STRING) {
        throw new GraphQLError(
          `Can only validate string percents but got a: ${ast.kind}`
        );
      }

      return new Decimal(ast.value);
    },

    extensions: {
      codegenScalarType: 'Percent',
    },
  });

export type CurrencyUSD = Decimal;
export const GraphQLCurrencyUSD: GraphQLScalarType =
  /*#__PURE__*/ new GraphQLScalarType({
    name: 'CurrencyUSD',

    description: 'Our Custom CurrencyUSD representation',

    serialize(value) {
      if (!Decimal.isDecimal(value)) {
        throw new TypeError(
          `CurrencyUSD value is not Decimal: ${value as string}`
        );
      }

      // we want to guarantee a minimum of two decimal places, but permit as many as the
      // sender wants to define, up to a max of 10 (which is the maximum precision we support
      // for GRAT annuity payments, but we don't want to allow infinite precision for
      // overflow concern reasons)
      const decimalPlaces = clamp(value.decimalPlaces(), 2, 10);
      return value.toFixed(decimalPlaces);
    },

    parseValue(value) {
      if (typeof value !== 'string') {
        throw new TypeError(
          `CurrencyUSD value is not string: ${value as string}`
        );
      }

      return new Decimal(value);
    },

    parseLiteral(ast) {
      if (ast.kind !== Kind.STRING) {
        throw new GraphQLError(
          `Can only validate string CurrencyUSD but got a: ${ast.kind}`
        );
      }

      return new Decimal(ast.value);
    },

    extensions: {
      codegenScalarType: 'CurrencyUSD',
    },
  });

export type GraphQLDecimal = Decimal;
export const GraphQLDecimal: GraphQLScalarType =
  /*#__PURE__*/ new GraphQLScalarType({
    name: 'Decimal',

    description: 'Our GraphQLDecimal representation',

    serialize(value) {
      if (!Decimal.isDecimal(value)) {
        throw new TypeError(
          `GraphQLDecimal value is not Decimal: ${value as string}`
        );
      }

      // we want to guarantee a minimum of two decimal places, but permit as many as the
      // sender wants to define, up to a max of 10 (which is the maximum precision we support
      // for GRAT annuity payments, but we don't want to allow infinite precision for
      // overflow concern reasons)
      const decimalPlaces = clamp(
        value.decimalPlaces(),
        2,
        GRAT_ANNUITIES_SUPPORTED_DECIMAL_PLACES
      );
      return value.toFixed(decimalPlaces);
    },

    parseValue(value) {
      if (typeof value !== 'string') {
        throw new TypeError(
          `GraphQLDecimal value is not string: ${value as string}`
        );
      }

      return new Decimal(value);
    },

    parseLiteral(ast) {
      if (ast.kind !== Kind.STRING) {
        throw new GraphQLError(
          `Can only validate string GraphQLDecimal but got a: ${ast.kind}`
        );
      }

      return new Decimal(ast.value);
    },

    extensions: {
      codegenScalarType: 'GraphQLDecimal',
    },
  });

export type Time = Date;
export const GraphQLTime: GraphQLScalarType =
  /*#__PURE__*/ new GraphQLScalarType({
    name: 'Time',

    description: 'Our Date representation',

    serialize(value) {
      if (value instanceof Date) {
        return value.toISOString();
      }

      throw new Error('Cannot save a non-Date value');
    },

    parseValue(value) {
      if (typeof value !== 'string') {
        throw new TypeError(`Date value is not string: ${value as string}`);
      }

      return new Date(value);
    },

    parseLiteral(ast) {
      if (ast.kind !== Kind.STRING) {
        throw new GraphQLError(
          `Can only validate string Date but got a: ${ast.kind}`
        );
      }

      return new Date(ast.value);
    },

    extensions: {
      codegenScalarType: 'Time',
    },
  });

export const scalarsMap = {
  Percent: GraphQLPercent,
  CurrencyUSD: GraphQLCurrencyUSD,
  Time: GraphQLTime,
  // need to call this "GraphQLDecimal" rather than just Decimal because it conflicts with the Decimal type from decimal.js
  // and we reference that a lot more than we reference this
  Decimal: GraphQLDecimal,
};
